import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

function LogoFallback({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="#00182A"/>
      <text x="24" y="32" textAnchor="middle" fill="white" fontSize="18" fontWeight="800" fontFamily="Montserrat,sans-serif">RH</text>
      <circle cx="38" cy="10" r="6" fill="#00C389"/>
      <path d="M35 10 L38 7 L41 10 L38 13 Z" fill="white"/>
    </svg>
  );
}

function Logo({ size = 36 }: { size?: number }) {
  const [error, setError] = useState(false);
  if (error) return <LogoFallback size={size} />;
  return (
    <img
      src="/logo.png"
      alt="Logo"
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className="object-contain rounded-lg flex-shrink-0"
      onError={() => setError(true)}
    />
  );
}

// Navegación admin/hr
const ADMIN_NAV = [
  { to: '/dashboard',       icon: 'dashboard',       label: 'Dashboard' },
  { to: '/employees',       icon: 'groups',           label: 'Empleados' },
  { to: '/attendance',      icon: 'event_available',  label: 'Asistencia' },
  { to: '/payroll',         icon: 'payments',         label: 'Planillas' },
  { to: '/bonuses',         icon: 'redeem',           label: 'Bonos' },
  { to: '/justifications',  icon: 'rate_review',      label: 'Justificaciones' },
  { to: '/liquidation',     icon: 'handshake',        label: 'Liquidaciones' },
  { to: '/reports',         icon: 'analytics',        label: 'Reportes' },
  { to: '/legal',           icon: 'gavel',            label: 'Legal' },
  { to: '/settings',        icon: 'settings',         label: 'Configuración' },
];

// Navegación empleado (solo su portal)
const EMPLOYEE_NAV = [
  { to: '/mi-asistencia',   icon: 'calendar_month',   label: 'Mi Asistencia' },
];

export function AppLayout() {
  const { company, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);

  const navItems = profile?.role === 'employee'
    ? EMPLOYEE_NAV
    : ADMIN_NAV;

  async function handleSignOut() {
    setSigningOut(true);
    await signOut();
    navigate('/login');
  }

  return (
    <div className="min-h-screen bg-surface flex">
      <aside className="h-screen w-64 fixed left-0 top-0 bg-[#00182A] flex flex-col py-6 px-4 z-50 border-r border-white/10">
        <div className="mb-8 px-2 flex items-center gap-3">
          <Logo size={38} />
          <div>
            <h1 className="text-base font-black tracking-tight text-[#00C389] font-headline leading-tight">
              RRHH Up Solutions
            </h1>
            <p className="text-[9px] uppercase tracking-widest text-white/50 font-bold">
              Gestión de Planillas
            </p>
          </div>
        </div>

        {company && (
          <div className="mx-2 mb-6 p-3 bg-white/10 rounded-xl border border-white/10">
            <p className="text-xs font-bold text-[#00C389] truncate">{company.name}</p>
            <p className="text-[10px] text-white/60 mt-0.5">
              {company.regime === 'general' ? 'Régimen General' :
               company.regime === 'pequeña_empresa' ? 'REMYPE - Pequeña Empresa' :
               'REMYPE - Microempresa'}
            </p>
          </div>
        )}

        <nav className="flex-1 space-y-0.5">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 text-sm font-medium ${
                  isActive
                    ? 'text-[#00C389] font-bold border-r-4 border-[#00C389] bg-white/10'
                    : 'text-white/60 hover:text-[#00C389] hover:bg-white/10'
                }`
              }
            >
              <span className="material-symbols-outlined text-[22px]">{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-[#00C389] flex items-center justify-center text-[#00182A] font-bold text-sm flex-shrink-0">
              {profile?.full_name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-white truncate">{profile?.full_name || 'Admin'}</p>
              <p className="text-[10px] text-white/50 uppercase tracking-tighter capitalize">{profile?.role || 'admin'}</p>
            </div>
            <button onClick={handleSignOut} disabled={signingOut} className="p-1.5 text-white/40 hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10" title="Cerrar sesión">
              <span className="material-symbols-outlined text-[18px]">logout</span>
            </button>
          </div>
          <div className="mt-2 px-2 pb-1">
            <p className="text-[9px] text-white/30 text-center uppercase tracking-widest">Powered by RRHH Up Solutions</p>
          </div>
        </div>
      </aside>

      <div className="ml-64 flex-1 flex flex-col min-h-screen">
        <header className="fixed top-0 right-0 w-[calc(100%-16rem)] z-40 glass-header border-b border-slate-200/20 h-14 flex items-center justify-between px-8">
          <div className="flex items-center bg-surface-container-low px-4 py-1.5 rounded-full w-80">
            <span className="material-symbols-outlined text-slate-400 mr-2 text-[18px]">search</span>
            <input className="bg-transparent border-none focus:ring-0 text-sm w-full outline-none placeholder:text-slate-400" placeholder="Buscar empleados, planillas..." />
          </div>
          <div className="flex items-center gap-4">
            <button className="p-2 text-slate-500 hover:text-blue-600 transition-colors">
              <span className="material-symbols-outlined text-[22px]">notifications</span>
            </button>
            <button className="p-2 text-slate-500 hover:text-blue-600 transition-colors">
              <span className="material-symbols-outlined text-[22px]">help_outline</span>
            </button>
            <div className="h-7 w-px bg-outline-variant/30" />
            <div className="text-right">
              <p className="text-sm font-bold text-slate-900 leading-tight">{company?.name || 'Empresa'}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-tighter">RUC: {company?.ruc || '—'}</p>
            </div>
          </div>
        </header>

        <main className="mt-14 flex-1 overflow-auto">
          <div className="animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
